/**
 * Historical temperaments, generated from their defining chain of fifths.
 *
 * Rather than hard-coding published cent tables (which are rounded, and easy to
 * typo), each temperament is defined by how much each of the twelve fifths
 * around the circle departs from the equal-tempered 700-cent fifth. The cent
 * offsets are then derived. This makes each definition auditable against the
 * historical recipe, and gives full precision.
 *
 * Invariant: twelve fifths must close the circle exactly, so the deviations
 * must sum to zero. `test/temperaments.test.ts` enforces this and also checks
 * the derived tables against published values.
 */

/** Cents by which a just 3:2 fifth exceeds the equal-tempered fifth. */
export const PURE_FIFTH_DEV = 1200 * Math.log2(3 / 2) - 700;

/** Pythagorean comma: twelve just fifths minus seven octaves, in cents. */
export const PYTHAGOREAN_COMMA = 12 * (1200 * Math.log2(3 / 2)) - 7 * 1200;

/** Syntonic comma: four just fifths minus a just major third and two octaves. */
export const SYNTONIC_COMMA = 4 * (1200 * Math.log2(3 / 2)) - 2 * 1200 - 1200 * Math.log2(5 / 4);

/** Schisma: the small residue between the two commas. */
export const SCHISMA = PYTHAGOREAN_COMMA - SYNTONIC_COMMA;

/**
 * The circle of fifths, as step indices. Step i is the fifth rising from the
 * note i fifths above C to the note i+1 fifths above C:
 *
 *   0:C-G  1:G-D  2:D-A  3:A-E  4:E-B   5:B-F#
 *   6:F#-C# 7:C#-G# 8:G#-Eb 9:Eb-Bb 10:Bb-F 11:F-C
 *
 * Step 8 is the "wolf" slot in the meantone and Pythagorean families: spelled
 * G#-Eb it is really a diminished sixth, and absorbs whatever is left over.
 */
const STEPS = 12;

/** Pitch class (0 = C) reached by going `k` fifths up from C. */
function pitchClassAfterFifths(k: number): number {
  return (7 * k) % 12;
}

/**
 * Turn per-fifth deviations into a table of cent offsets from equal
 * temperament, indexed by pitch class with C = 0.
 */
function offsetsFromFifths(fifthDeviations: readonly number[]): number[] {
  if (fifthDeviations.length !== STEPS) {
    throw new Error(`expected ${STEPS} fifths, got ${fifthDeviations.length}`);
  }
  const offsets = new Array<number>(12).fill(0);
  let cumulative = 0;
  for (let k = 0; k < STEPS; k++) {
    offsets[pitchClassAfterFifths(k)] = cumulative;
    cumulative += fifthDeviations[k]!;
  }
  return offsets;
}

/** Build a deviation array where `tempered` steps get `dev` and the rest are pure. */
function temper(tempered: readonly number[], dev: number): number[] {
  const set = new Set(tempered);
  return Array.from({ length: STEPS }, (_, i) => (set.has(i) ? dev : PURE_FIFTH_DEV));
}

/**
 * Meantone family: eleven equal tempered fifths from Eb to G#, with the
 * leftover dumped into the G#-Eb wolf (step 8).
 */
function meantone(commaFraction: number): number[] {
  const dev = PURE_FIFTH_DEV - SYNTONIC_COMMA * commaFraction;
  const fifths = Array.from({ length: STEPS }, () => dev);
  fifths[8] = -11 * dev;
  return fifths;
}

export interface Temperament {
  readonly id: string;
  readonly name: string;
  readonly year: string;
  readonly description: string;
  /** Cents from equal temperament, indexed by pitch class, C = 0. */
  readonly offsets: readonly number[];
}

function define(
  id: string,
  name: string,
  year: string,
  description: string,
  fifthDeviations: readonly number[],
): Temperament {
  return { id, name, year, description, offsets: offsetsFromFifths(fifthDeviations) };
}

export const TEMPERAMENTS: readonly Temperament[] = [
  define(
    "equal",
    "Equal",
    "modern",
    "Twelve identical semitones. Every key equally usable, every third equally wide.",
    Array.from({ length: STEPS }, () => 0),
  ),
  define(
    "vallotti",
    "Vallotti",
    "1754",
    "Six fifths F-B narrowed by 1/6 Pythagorean comma, six pure. Gentle, very common on 18th-century-style instruments.",
    temper([11, 0, 1, 2, 3, 4], PURE_FIFTH_DEV - PYTHAGOREAN_COMMA / 6),
  ),
  define(
    "young2",
    "Young II",
    "1799",
    "Vallotti shifted one fifth sharp: C-F# narrowed by 1/6 Pythagorean comma. Slightly brighter flat keys.",
    temper([0, 1, 2, 3, 4, 5], PURE_FIFTH_DEV - PYTHAGOREAN_COMMA / 6),
  ),
  define(
    "werckmeister3",
    "Werckmeister III",
    "1691",
    "Four fifths (C-G, G-D, D-A, B-F#) narrowed by 1/4 Pythagorean comma. The classic well-temperament; all keys usable, each with its own colour.",
    temper([0, 1, 2, 5], PURE_FIFTH_DEV - PYTHAGOREAN_COMMA / 4),
  ),
  define(
    "kirnberger3",
    "Kirnberger III",
    "1779",
    "C-G-D-A-E narrowed by 1/4 syntonic comma giving a pure C-E third; the schisma is absorbed by F#-C#.",
    (() => {
      const fifths = temper([0, 1, 2, 3], PURE_FIFTH_DEV - SYNTONIC_COMMA / 4);
      fifths[6] = PURE_FIFTH_DEV - SCHISMA;
      return fifths;
    })(),
  ),
  define(
    "kellner",
    "Kellner (Bach)",
    "1975",
    "Five fifths narrowed by 1/5 Pythagorean comma. Kellner's reconstruction of the temperament implied by Bach's title-page flourish.",
    temper([0, 1, 2, 3, 5], PURE_FIFTH_DEV - PYTHAGOREAN_COMMA / 5),
  ),
  define(
    "meantone4",
    "1/4-comma meantone",
    "c. 1550",
    "Pure major thirds in the home keys, at the price of a brutal G#-Eb wolf fifth. The Renaissance standard.",
    meantone(1 / 4),
  ),
  define(
    "meantone6",
    "1/6-comma meantone",
    "c. 1700",
    "Milder meantone. Thirds slightly wide, wolf survivable, more keys in play.",
    meantone(1 / 6),
  ),
  define(
    "pythagorean",
    "Pythagorean",
    "medieval",
    "Eleven pure fifths, one savage wolf at G#-Eb. Pure fifths and fourths, very wide thirds.",
    temper([8], -11 * PURE_FIFTH_DEV),
  ),
];

const BY_ID = new Map(TEMPERAMENTS.map((t) => [t.id, t]));

export function temperamentById(id: string): Temperament {
  const found = BY_ID.get(id);
  if (!found) throw new Error(`unknown temperament: ${id}`);
  return found;
}

/**
 * Cent offset to apply to a pitch class, normalised so that `anchorPitchClass`
 * lands exactly on its equal-tempered frequency.
 *
 * Tuners set the reference pitch on one note (conventionally A) and derive the
 * rest, so the published tables — which are usually normalised to C — must be
 * re-anchored or the whole instrument sits a few cents off the fork.
 */
export function offsetForPitchClass(
  temperament: Temperament,
  pitchClass: number,
  anchorPitchClass: number,
): number {
  const pc = ((pitchClass % 12) + 12) % 12;
  const anchor = ((anchorPitchClass % 12) + 12) % 12;
  return temperament.offsets[pc]! - temperament.offsets[anchor]!;
}
